// WHO SEES THE DELIVERY SIGNAL, BY AUDIENCE (dossier v3, Vinnie 3 Nov 2026).
//
// Replaces the two board booleans (deliverySignalEnabled, costInSignal — migration
// 0222) with two audiences an admin sets in Settings → Features. The Schedule read
// (the card's forecast + pace) and the Budget read (the whole project's cost) are
// chosen SEPARATELY, and Budget can never be set wider than Schedule. The hard floor
// is fixed, not a choice: an EXTERNAL VENDOR never sees either signal (money and
// internal steering stay in the company), and an EXECUTIVE always counts as
// a manager. "Everyone" means every internal board member INCLUDING the doer/team
// member — the one case that widens past the manager tier.
//
// Single source of truth: both the card (IssueModal, who renders the band) and the
// admin surface (Settings, who sets it) read the audience and the bound from here, so
// they can never drift. Back-compatible with the 0222 booleans while boards migrate.

export type SignalAudience = 'off' | 'managers' | 'all';

const RANK: Record<SignalAudience, number> = { off: 0, managers: 1, all: 2 };

const clean = (v: unknown): SignalAudience | null =>
  v === 'off' || v === 'managers' || v === 'all' ? v : null;

/** Board meta fields this module reads (new audiences + the 0222 booleans it falls back to). */
interface SignalMeta {
  scheduleSignalAudience?: string | null;
  budgetSignalAudience?: string | null;
  deliverySignalEnabled?: boolean;
  costInSignal?: boolean;
}

/** The schedule audience for a board, back-compatible with the 0222 boolean. */
export function scheduleAudienceOf(meta: SignalMeta): SignalAudience {
  return clean(meta.scheduleSignalAudience) ?? (meta.deliverySignalEnabled === false ? 'off' : 'managers');
}

/**
 * The budget audience, back-compatible with the 0222 boolean and NEVER wider than
 * the schedule audience — you cannot see the project cost on a card whose schedule
 * read you cannot see.
 */
export function budgetAudienceOf(meta: SignalMeta): SignalAudience {
  const raw = clean(meta.budgetSignalAudience) ?? (meta.costInSignal === true ? 'managers' : 'off');
  const sched = scheduleAudienceOf(meta);
  return RANK[raw] > RANK[sched] ? sched : raw;
}

/** True when `a` is a wider audience than `b` — used to disable out-of-range picker options. */
export const audienceWiderThan = (a: SignalAudience, b: SignalAudience): boolean => RANK[a] > RANK[b];

export interface SignalViewer {
  /** An external vendor — never sees either signal, whatever the audience. */
  isVendor: boolean;
  /** The viewer's company role (src/company-role.ts), or null. */
  companyRole: string | null;
  /** The viewer's board role reaches the steering tier (PM / lead / manager / admin). */
  maySeeOdds: boolean;
}

/** Resolve what one viewer sees of the two signals, given the board's two audiences. */
export function signalVisibility(
  schedule: SignalAudience,
  budget: SignalAudience,
  v: SignalViewer,
): { seeSchedule: boolean; seeBudget: boolean } {
  // The manager tier: a vendor never, an Executive always, a team member
  // never, everyone else by their board role (maySeeOdds — PM/lead/manager/admin).
  const managerTier = v.isVendor
    ? false
    : v.companyRole === 'executive_director'
      ? true
      : v.companyRole === 'team_member'
        ? false
        : v.maySeeOdds;
  // "Everyone" is every internal board member — a vendor is the only one shut out.
  const internal = !v.isVendor;
  const see = (aud: SignalAudience) => (aud === 'all' ? internal : aud === 'managers' ? managerTier : false);
  const seeSchedule = see(schedule);
  // Budget is bounded ≤ schedule, but gate on seeSchedule too so it can never leak
  // past the schedule read for this particular viewer.
  const seeBudget = seeSchedule && see(budget);
  return { seeSchedule, seeBudget };
}
